/**
 * What a screen says when messages were accepted and nothing has come back —
 * now that it can know whether anything ever has.
 *
 * Slice 20 gave the Overview `NO_OUTCOME_REPORTED_COPY`: *no delivery report has
 * come back*, hedged with *probably*, because a window's counts cannot tell late
 * reports from reports that never once arrived. Slice 19 adds that fact —
 * `DeliveryReports`, when Wend last stored a report per channel — and this file
 * is the one place the two are put together, so the Overview tiles, the
 * not-delivered card and a campaign's results cannot describe the same silence
 * three ways.
 *
 * ## THREE ANSWERS, AND ONLY ONE OF THEM IS A HEDGE
 *
 *   never            no report has ever been stored. Said flatly, as a setup
 *                    fact — `DELIVERY_REPORTS_NEVER_COPY` — with the first
 *                    missing step beside it on email. Hedging a certainty is
 *                    how a reader learns to wait for something that is not
 *                    coming, and the live deployment's reader had been told
 *                    "probably" for weeks;
 *   received_before  reports have arrived. The setup has worked, so "a setup
 *                    step" is the wrong first thought; the sentence names the
 *                    three causes left — mail still delayed, mail sent before
 *                    reports were set up, or reports that stopped — and the
 *                    date that tells the last two apart;
 *   not_known        the response carried no `deliveryReports` — an API older
 *                    than slice 19. Slice 20's hedged sentence, word for word,
 *                    because it is still the most that can honestly be said.
 *
 * **And a fourth wording, for complaints.** A window whose only reports are spam
 * complaints is still one with no delivery or bounce reported, and the screens
 * still hide those figures — but it has had a report, so "never" cannot be true
 * and slice 20's sentence, which says no complaint came back, is false.
 * `NO_OUTCOME_BUT_COMPLAINTS_COPY` is said instead, whatever `reports` holds.
 *
 * Data, not JSX, for `recipient-experience.ts`'s reason: several screens print
 * it, and a component with a `variant` prop grows a fourth wording the first
 * time one of them needs a different layout.
 */
import {
  DELIVERY_REPORTS_NEVER_COPY,
  EMAIL_DELIVERY_REPORTS_SETUP_COPY,
  NO_OUTCOME_BUT_COMPLAINTS_COPY,
  NO_OUTCOME_REPORTED_COPY,
  deliveryReportsReceivedBefore,
  type Channel,
  type DeliveryReports,
  type DeliveryReportsSetup,
} from '@wend/shared';

import { formatDateTime } from './format';

/**
 * The setup steps whose repair is a form on Integrations. The other three are
 * not: `no_public_address` and `topic_not_accepted` are the deployment's
 * settings, and `ready` has nothing left to set up — a link under "the first
 * missing step is on the Wend deployment itself" sent the reader to a screen
 * that cannot fix it (review).
 */
const FIXED_ON_INTEGRATIONS: ReadonlySet<DeliveryReportsSetup> = new Set([
  'no_sending_account',
  'no_topic',
  'not_set_up',
]);

export interface UnreportedExplanation {
  readonly state: 'never' | 'received_before' | 'not_known';
  readonly title: string;
  readonly paragraphs: readonly string[];
  /**
   * True when the repair is on the Integrations screen: email, with a first
   * missing step that is a form there. A screen shows the link only then — for a
   * deployment setting, or a workspace whose reports stopped, the repair is
   * elsewhere, and a link to a form that cannot fix it would say otherwise.
   */
  readonly pointsAtSetup: boolean;
}

/** Total over the answers above. */
export function unreportedExplanation(
  channel: Channel,
  reports: DeliveryReports | null,
  /**
   * Spam complaints Amazon reported about the messages the screen is counting.
   * Required rather than defaulted, so a screen cannot forget the one count that
   * changes the sentence; WhatsApp, which has no complaint, passes `0`.
   */
  complaints: number,
): UnreportedExplanation {
  if (channel === 'email' && complaints > 0) {
    return {
      state: reports === null ? 'not_known' : 'received_before',
      title: NO_OUTCOME_BUT_COMPLAINTS_COPY.short,
      paragraphs: [NO_OUTCOME_BUT_COMPLAINTS_COPY.detail],
      // The link only where Wend can see delivery reports are not set up: that
      // is the repair a complaint leaves open. Set up already, the gap is mail
      // sent before, and a link to a finished form would say otherwise.
      pointsAtSetup: reports !== null && FIXED_ON_INTEGRATIONS.has(reports.email.setup),
    };
  }

  if (reports === null) {
    return {
      state: 'not_known',
      title: NO_OUTCOME_REPORTED_COPY[channel].short,
      paragraphs: [NO_OUTCOME_REPORTED_COPY[channel].detail],
      pointsAtSetup: false,
    };
  }

  const lastReceivedAt = reports[channel].lastReceivedAt;
  if (lastReceivedAt === null) {
    return {
      state: 'never',
      title: DELIVERY_REPORTS_NEVER_COPY[channel].short,
      paragraphs:
        channel === 'email'
          ? [
              DELIVERY_REPORTS_NEVER_COPY.email.detail,
              EMAIL_DELIVERY_REPORTS_SETUP_COPY[reports.email.setup],
            ]
          : [DELIVERY_REPORTS_NEVER_COPY.whatsapp.detail],
      pointsAtSetup: channel === 'email' && FIXED_ON_INTEGRATIONS.has(reports.email.setup),
    };
  }

  return {
    state: 'received_before',
    title: NO_OUTCOME_REPORTED_COPY[channel].short,
    paragraphs: [deliveryReportsReceivedBefore(channel, formatDateTime(lastReceivedAt))],
    pointsAtSetup: false,
  };
}
